import IconSearch from 'assets/icons/search.svg';
import cn from 'classnames';
import { useShimmerDataUrl } from 'hooks/common/use-shimmer-data-url';
import useSynchoniseVariantImage from 'hooks/product/use-synchronise-variant-image';
import { ProductGallery } from 'modules/product-details/components/product-details-header';
import { Variant } from 'modules/product-details/types';
import Image from 'next/image';
import React, { useEffect, useRef } from 'react';
import Slider from 'react-slick';
import { generalImageLoader } from 'utils/image-loader';
import ImagesCarousel from '../full-screen-gallery/images-carousel';
import SegmentBanner from '../segment-banner';

interface PropTypes {
  imageBackgroundColor?: string;
  title: string;
  productGallery: ProductGallery[];
  selectedGalleryItemIndex: number;
  selectedVariantId: string | null;
  svgLayerRef: React.MutableRefObject<number | undefined>;
  onOpenGallery: (index: number) => void;
  onSwipe?: (index: number) => void;
  setSelectedGalleryItemIndex: React.Dispatch<React.SetStateAction<number>>;
  ribbonText?: React.ReactNode;
  variants: Variant[];
}

const ImagesGalleryDesktop: React.FC<PropTypes> = ({
  imageBackgroundColor,
  title,
  variants,
  productGallery,
  selectedGalleryItemIndex,
  selectedVariantId,
  svgLayerRef,
  onOpenGallery,
  onSwipe,
  setSelectedGalleryItemIndex,
  ribbonText,
}) => {
  const sliderRef = useRef<Slider>(null);

  useEffect(() => {
    if (sliderRef.current !== null) {
      // Slide to image
      sliderRef.current.slickGoTo(selectedGalleryItemIndex);
    }
  }, [selectedGalleryItemIndex]);

  const sliderOptions = {
    slidesToShow: 1,
    arrows: false,
    // useCSS: true,
    autoplay: false,
    infinite: false,
    draggable: false,
    afterChange: function (index: number) {
      if (onSwipe) onSwipe(index);

      const videos = document.querySelectorAll<HTMLVideoElement>('video');
      Array.from(videos).map((video) => {
        return video.pause();
      });

      const video = document.querySelector<HTMLVideoElement>(
        '.slick-active video'
      );
      if (video) {
        video.play();
      }
    },
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          arrows: true,
          infinite: true,
          //useCSS: true,
          draggable: true,
        },
      },
    ],
  };

  const blurDataURL = useShimmerDataUrl({
    width: 64,
    height: 64,
  });

  useSynchoniseVariantImage({
    productGallery,
    selectedGalleryItemIndex,
    selectedVariantId,
    sliderRef,
    svgLayerRef,
    variants,
    setSelectedGalleryItemIndex,
  });

  return (
    <div className="w-full">
      <div className={cn('relative flex-1 rounded-lg p-4')}>
        <Slider {...sliderOptions} ref={sliderRef}>
          {productGallery.map((image, index) => {
            if (!image.imageData) return null;
            return (
              <button
                key={index}
                className="group relative cursor-pointer"
                onClick={() => {
                  setSelectedGalleryItemIndex(index);
                  sliderRef.current?.slickGoTo(index);
                  onOpenGallery(selectedGalleryItemIndex);
                }}
              >
                <Image
                  alt={image.imageData.altText || title}
                  blurDataURL={blurDataURL}
                  className="mx-auto rounded-lg"
                  loader={generalImageLoader}
                  placeholder="blur"
                  priority
                  src={image.imageData.originalSrc}
                  width={400}
                  height={400}
                />
              </button>
            );
          })}
        </Slider>
        <button
          className="bg-grey-50 absolute right-0 bottom-0 mr-3 mb-3 flex h-8 w-8 items-center justify-center rounded-full"
          onClick={() => onOpenGallery(selectedGalleryItemIndex)}
        >
          <IconSearch className="text-text-secondary h-3.5 w-3.5" />
        </button>
      </div>
      {ribbonText && <SegmentBanner>{ribbonText}</SegmentBanner>}
      <ImagesCarousel
        imageBackgroundColor={imageBackgroundColor}
        images={productGallery}
        setSelectedGalleryItemIndex={setSelectedGalleryItemIndex}
        sliderRef={sliderRef}
        title={title}
      />
    </div>
  );
};

export default ImagesGalleryDesktop;
